//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { Fragment, useState, useCallback, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { useHistory, useLocation } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { PATH_DASHBOARD } from '../system/constants';
import { AppContext } from '../common/contexts';
import { LayoutContext } from '../common/contexts';
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { setClassNameInDefns } from '../common/definitionHelper';
import { DASHBOARD_ID_MY_SETTINGS, DASHBOARD_ID_MY_SETTINGS_SUBSCRIPTION, DASHBOARD_MY_SETTINGS } from '../common/constants';
import { BASE_URL } from '../common/constants';
import { handleCloseSnackbar } from '../common/snackbarHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdSnackbar from '../common/elemental/AfitdSnackbar';
import TopNav from '../topNav/TopNav';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import MySettings from '../empDashboardMySettings';
import AccountSetupLeftNavigation from './subComponents/AccountSetupLeftNavigation';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    dashboardContainer: { overflow: 'hidden', height: '100%', minHeight: '100%', flexWrap: 'unset' },
    dashboardHeader: { maxHeight: '81px' },
    dashboardContent: { flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingRight: '64px', [theme.breakpoints.down('md')]: { paddingRight: '0px', justifyContent: 'center' } },
    dashboardRight: { height: '100%', [theme.breakpoints.down('lg')]: { height: '735px' }, [theme.breakpoints.down('md')]: { height: '100%' } },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { /*props,*/ classes, snackbarMessage, setSnackbarMessage, snackbarErrorMessage, setSnackbarErrorMessage } = params;
    // const {} = props;

    let defns = {
        dashboardContainer: { container: true, direction: 'column' },
        dashboardHeader: { item: true, lg: 12, md: 12, sm: 12, xs: 12 },
        dashboardContent: { item: true, container: true },
        dashboardLeft: { item: true, lg: 3, md: 0, sm: 0, xs: 0 },
        dashboardRight: { item: true, lg: 9, md: 10, sm: 11, xs: 11 },

        snackbar: {
            message: { successMessage: snackbarMessage, errorMessage: snackbarErrorMessage },
            onClose: handleCloseSnackbar.bind(null, setSnackbarMessage, setSnackbarErrorMessage),
        },
        leftNavigation: {},
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const AccountSetupLayout = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const layoutContext = useContext(LayoutContext);
    const userId = _get(appContext, 'authUser.uid'); //TODO do not need this anymore because userId is set in appContext directly
    // const employerId = _get(appContext, 'employerId');
    const pendingAccountSetup = _get(appContext, 'pendingAccountSetup');
    const classes = useStyles();
    const defaultPage = DASHBOARD_MY_SETTINGS;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    // const [dashboard, setDashboard] = useState(DASHBOARD_MY_SETTINGS);
    const [dashboard, setDashboard] = useState(defaultPage);
    const [snackbarMessage, setSnackbarMessage] = useState();
    const [snackbarErrorMessage, setSnackbarErrorMessage] = useState();

    const location = useLocation();

    //---------------------------------------------------------------------
    // TEMP TEMP TEMP TEMP TEMP TEMP
    //---------------------------------------------------------------------
    //---------------------------------------------------------------------
    //---------------------------------------------------------------------
    //---------------------------------------------------------------------
    const history = useHistory();
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const setCurrentDashboard = useCallback(
        newPageId => {
            // setDashboard(newDashboard);
            history.push(`${BASE_URL}${PATH_DASHBOARD}/${newPageId}`);
        },
        [history],
    );

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // useEffect(() => {
    //     let unsubscribeOnSnapshot = null;

    //     if (isRequestLoadProfile) {
    //         // load profile data
    //         unsubscribeOnSnapshot = loadProfile(employerId, setProfileData);
    //         setIsRequestLoadProfile(false);
    //     }

    //     // unsubscribe realtime update on unmount
    //     return () => {
    //         unsubscribeRealtimeUpdates([unsubscribeOnSnapshot]);
    //     };
    // }, [isRequestLoadProfile, employerId]);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({ props, classes, snackbarMessage, setSnackbarMessage, snackbarErrorMessage, setSnackbarErrorMessage });

    useEffect(() => {
        const { state } = location;
        if (!pendingAccountSetup) {
            // if coming to this page but user has already set up account, redirect to Billing and Subscription page
            history.push(`${BASE_URL}${PATH_DASHBOARD}/${DASHBOARD_ID_MY_SETTINGS}/${DASHBOARD_ID_MY_SETTINGS_SUBSCRIPTION}`);
        } else if (state?.state?.currentDashboard) {
            return setCurrentDashboard(state.state.currentDashboard);
        }
    }, [location, history, pendingAccountSetup, setCurrentDashboard]);

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return !pendingAccountSetup ? null : (
        <LayoutContext.Provider
            value={{
                ...layoutContext,
                userId,
                dashboard,
                tempSetDashboard: setDashboard,
                setCurrentDashboard,
                setSnackbarMessage,
                setSnackbarErrorMessage,
            }}
        >
            <Fragment>
                <Grid {...defns.dashboardContainer}>
                    <Grid {...defns.dashboardHeader}>
                        <TopNav />
                    </Grid>
                    <Grid {...defns.dashboardContent}>
                        <Grid {...defns.dashboardLeft}>
                            <AccountSetupLeftNavigation {...defns.leftNavigation} />
                        </Grid>
                        <Grid {...defns.dashboardRight}>
                            <MySettings />
                        </Grid>
                    </Grid>
                </Grid>
                <AfitdSnackbar {...defns.snackbar} />
            </Fragment>
        </LayoutContext.Provider>
    );
};

export default AccountSetupLayout;
